import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';

/** Every step, in order. */
const ORDER = ['agency', 'items', 'details', 'needs', 'contact', 'source', 'review'];
/** Where the answers wait while Shopify checks the form, so an error does not lose them. */
const STORAGE_KEY = 'public-safety-builder-answers';
/** Start of the sessionStorage names shared with snippets/public-safety-return.liquid ("-url", "-added") and "-cart". */
const RETURN_KEY = 'public-safety-return';
/** A real person cannot finish the steps this fast. */
const MINIMUM_SECONDS = 3;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/**
 * What was answered about one chosen item.
 * @typedef {object} Detail
 * @property {string} quantity - As typed; '' means not sure.
 * @property {Record<string, string>} choices - Follow-up question key to the chosen answer.
 */

/**
 * @typedef {object} Answers
 * @property {string} agencyType
 * @property {string[]} items - What they are looking for, in the order chosen.
 * @property {string[]} later - The order-now items they will build later.
 * @property {Record<string, Detail>} details - Item name to its details, for the items that have any.
 * @property {string} needs
 * @property {string} agency
 * @property {string} name
 * @property {string} title
 * @property {string} email
 * @property {string} phone
 * @property {string} location
 * @property {string} date - YYYY-MM-DD, or ''.
 * @property {string} source
 * @property {string} sourceOther
 */

/**
 * One line of the Shopify cart, as cart.js returns it.
 * @typedef {object} CartLine
 * @property {string} title
 * @property {string} handle - The product handle, used to tell which item the line belongs to.
 * @property {number} quantity
 * @property {number} cents - The line's price in minor units.
 */

/**
 * @typedef {object} Cart
 * @property {CartLine[]} lines
 * @property {number} count - How many items, quantities added up.
 * @property {number} cents - The subtotal in minor units.
 * @property {string} currency
 */

/**
 * @typedef {object} PublicSafetyBuilderRefs
 * @property {HTMLElement} [thanks]
 * @property {HTMLElement} [intro]
 * @property {HTMLElement} [serverError]
 * @property {HTMLElement} [fallback]
 * @property {HTMLElement} [app]
 * @property {HTMLElement} [stage]
 * @property {HTMLElement} [chips]
 * @property {HTMLElement} [empty]
 * @property {HTMLElement} [progress]
 * @property {HTMLElement} [details]
 * @property {HTMLElement} [noDetails]
 * @property {HTMLTemplateElement} [detailTemplate]
 * @property {HTMLTemplateElement} [orderTemplate]
 * @property {HTMLTemplateElement} [moneyFormat]
 * @property {HTMLElement} [addedNotice]
 * @property {HTMLButtonElement} [restart]
 * @property {HTMLElement} [restartBox]
 * @property {HTMLElement} [restartQuestion]
 * @property {HTMLElement} [restartError]
 * @property {HTMLElement} [announce]
 * @property {HTMLTextAreaElement} [needs]
 * @property {HTMLInputElement} [agency]
 * @property {HTMLInputElement} [name]
 * @property {HTMLInputElement} [title]
 * @property {HTMLInputElement} [email]
 * @property {HTMLInputElement} [phone]
 * @property {HTMLInputElement} [location]
 * @property {HTMLInputElement} [date]
 * @property {HTMLElement} [sourceOtherField]
 * @property {HTMLInputElement} [sourceOther]
 * @property {HTMLElement} [summary]
 * @property {HTMLButtonElement} [submit]
 * @property {HTMLElement} [sending]
 * @property {HTMLButtonElement} [back]
 * @property {HTMLButtonElement} [next]
 * @property {HTMLTextAreaElement} [body]
 * @property {HTMLInputElement} [trap]
 */

/**
 * Step-by-step Public Safety quote request, built on the patterns of the quote builder
 * (assets/quote-builder.js) but kept separate from it. It sends Shopify's contact form: the
 * name, email and phone go in their own fields and every answer is written into the message
 * body, one labeled line each. Those labels are fixed English because the email is read by
 * the store.
 *
 * It is also a hub: an "order now" item opens one of the other builders, and the customer
 * comes back here. Their place is kept in sessionStorage, and the cart is read from Shopify
 * (cart.js) every time the page is shown, so "In cart" always reflects the real cart.
 *
 * After Shopify accepts the form the section renders only the thank-you message, so every
 * ref is optional.
 *
 * @extends {Component<PublicSafetyBuilderRefs>}
 */
class PublicSafetyBuilder extends Component {
  #current = 0;
  #agencyType = '';
  /** @type {string[]} */
  #items = [];
  /** The order-now items the customer will build later instead of now. @type {Set<string>} */
  #later = new Set();
  /** Kept for every item ever chosen, so taking one off and putting it back loses nothing. @type {Record<string, Detail>} */
  #details = {};
  #source = '';
  /** What Shopify says is in the cart. Null until it has answered. @type {Cart | null} */
  #cart = null;
  /**
   * Set when the customer has just used Add to cart in another builder: how many items the
   * cart held before. Null otherwise.
   * @type {number | null}
   */
  #addedFrom = null;
  #started = Date.now();
  #sending = false;
  /** @type {AbortController | undefined} */
  #listeners;

  connectedCallback() {
    super.connectedCallback();
    const { app, stage, fallback, date, thanks } = this.refs;
    const form = this.querySelector('form');

    // Sent: only the thank-you message is on the page.
    if (!app || !stage || !form) {
      this.#forget();
      // Sent with something in the cart: on to the cart, as the button said.
      if (this.#take(`${RETURN_KEY}-cart`)) {
        window.location.assign(this.dataset.cartUrl || '/cart');
        return;
      }
      if (thanks && !thanks.hidden) thanks.focus({ preventScroll: true });
      return;
    }

    this.#listeners = new AbortController();
    const { signal } = this.#listeners;
    this.addEventListener('click', this.#onClick, { signal });
    form.addEventListener('input', this.#onInput, { signal });
    form.addEventListener('submit', this.#onSubmit, { signal });
    form.addEventListener('keydown', this.#onKeydown, { signal });
    // Coming back with the browser's back button: allow sending again, and read the cart again.
    window.addEventListener(
      'pageshow',
      (event) => {
        this.#setSending(false);
        if (event.persisted) this.#loadCart();
      },
      { signal }
    );

    this.#started = Date.now();
    if (date) date.min = this.#today();
    if (fallback) fallback.hidden = true;
    app.hidden = false;

    // The other builders link back to this address, and start again at "Building for...".
    this.#store(`${RETURN_KEY}-url`, window.location.pathname + this.#viewQuery());
    const added = this.#takeValue(`${RETURN_KEY}-added`);
    this.#addedFrom = added !== null && Number.isFinite(Number(added)) ? Number(added) : null;
    // Set again when the request is sent; an error from Shopify must not lead to the cart.
    this.#take(`${RETURN_KEY}-cart`);

    // Answers saved before leaving for another builder, or before Shopify returned an error.
    if (this.#restore()) {
      // They already spent the time on the first visit, so sending straight away is fine.
      this.#started = 0;
      // After an error: the review. After another builder: "Your order".
      const failed = stage.hasAttribute('data-restore');
      this.#current = failed ? this.#steps().length - 1 : Math.max(0, this.#steps().indexOf('details'));
      this.#render(false);
      if (failed) this.refs.serverError?.focus();
    } else {
      this.#render(false);
    }
    this.#loadCart();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners?.abort();
  }

  /** @returns {string} "?view=<template>" when the page was opened as a preview of a template, else ''. */
  #viewQuery() {
    const view = new URLSearchParams(window.location.search).get('view');
    return view ? `?view=${encodeURIComponent(view)}` : '';
  }

  /**
   * Writes to sessionStorage, or removes the key when the value is null. The hub works without it.
   * @param {string} key
   * @param {string | null} value
   */
  #store(key, value) {
    try {
      if (value === null) window.sessionStorage.removeItem(key);
      else window.sessionStorage.setItem(key, value);
    } catch {
      // Storage can be blocked; the customer then starts again after visiting another builder.
    }
  }

  /**
   * Reads a value from sessionStorage and removes it.
   * @param {string} key
   * @returns {string | null} The value, or null when there is none.
   */
  #takeValue(key) {
    try {
      const value = window.sessionStorage.getItem(key);
      window.sessionStorage.removeItem(key);
      return value;
    } catch {
      return null;
    }
  }

  /**
   * Reads a mark from sessionStorage and removes it.
   * @param {string} key
   * @returns {boolean} True when the mark was set.
   */
  #take(key) {
    return this.#takeValue(key) === '1';
  }

  /** @returns {string} Today's date where the customer is, as YYYY-MM-DD. */
  #today() {
    const now = new Date();
    const pad = (/** @type {number} */ value) => String(value).padStart(2, '0');
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  }

  /** @returns {boolean} True when the chosen source asks for details. */
  #sourceNeedsDetails() {
    const names = (this.dataset.otherSource ?? '').split(',').map((entry) => entry.trim().toLowerCase());
    return this.#source !== '' && names.includes(this.#source.toLowerCase());
  }

  /**
   * @param {string} key
   * @returns {HTMLElement[]} Every tile of a step.
   */
  #tiles(key) {
    return Array.from(this.querySelectorAll(`.hat-builder__tile[data-step="${key}"]`));
  }

  /**
   * @param {string} key
   * @returns {HTMLElement | null}
   */
  #panel(key) {
    return this.querySelector(`[data-panel="${key}"]`);
  }

  /**
   * @param {string} item
   * @returns {string} The item's follow-up: "quantity", "badges", "coins" or "none".
   */
  #followUp(item) {
    return this.#tiles('items').find((tile) => tile.dataset.value === item)?.dataset.followUp ?? 'quantity';
  }

  /**
   * @param {string} item
   * @returns {HTMLElement | undefined} The item's tile.
   */
  #itemTile(item) {
    return this.#tiles('items').find((tile) => tile.dataset.value === item);
  }

  /**
   * @param {string} item
   * @returns {boolean} True when the item is ordered in another builder instead of quoted.
   */
  #ordered(item) {
    const data = this.#itemTile(item)?.dataset;
    return data?.mode === 'order' && Boolean(data.link);
  }

  /** @returns {string[]} The chosen items we quote. Only these are emailed. */
  #quoteItems() {
    return this.#items.filter((item) => !this.#ordered(item));
  }

  /** @returns {string[]} The quoted items that have details to ask: all but "no details" ones. */
  #detailedItems() {
    return this.#quoteItems().filter((item) => this.#followUp(item) !== 'none');
  }

  /**
   * @param {string} item
   * @returns {boolean} True when the cart holds a product that counts as this item.
   */
  #inCart(item) {
    const handles = (this.#itemTile(item)?.dataset.handles ?? '').split(',').filter(Boolean);
    return Boolean(this.#cart?.lines.some((line) => handles.includes(line.handle)));
  }

  /** @returns {string[]} The order-now items that are not in the cart: the ones still to be built. */
  #laterItems() {
    return this.#items.filter((item) => this.#ordered(item) && !this.#inCart(item));
  }

  /** @returns {string[]} The steps. The contact details are always asked; they are needed for invoicing. */
  #steps() {
    return ORDER;
  }

  /** @param {number} cents */
  #money(cents) {
    const format = this.refs.moneyFormat?.content.textContent || '{{amount}}';
    return formatMoney(cents, format, this.#cart?.currency || this.dataset.currency || '');
  }

  /** @returns {string[]} The cart lines as text, "2 × Custom Tumbler - 20oz / Black: $50.00", then the subtotal. */
  #cartText() {
    const lines = this.#cart?.lines ?? [];
    if (!lines.length) return [];
    return [...lines.map((line) => `${line.quantity} × ${line.title}: ${this.#money(line.cents)}`), `${this.dataset.subtotalLabel ?? ''}: ${this.#money(this.#cart?.cents ?? 0)}`];
  }

  /** Reads the cart from Shopify, then refreshes everything that shows it. A failure leaves the page as it was. */
  async #loadCart() {
    try {
      const response = await fetch(`${this.dataset.cartUrl || '/cart'}.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const cart = await response.json();
      this.#cart = {
        lines: (Array.isArray(cart.items) ? cart.items : []).map((/** @type {Record<string, unknown>} */ line) => ({
          title: String(line.title ?? line.product_title ?? ''),
          handle: String(line.handle ?? ''),
          quantity: Number(line.quantity) || 0,
          cents: Number(line.final_line_price ?? line.line_price) || 0,
        })),
        count: Number(cart.item_count) || 0,
        cents: Number(cart.items_subtotal_price ?? cart.total_price) || 0,
        currency: String(cart.currency ?? ''),
      };
    } catch {
      return;
    }
    // Said only when Shopify's cart is bigger than it was before the other builder was used.
    if (this.refs.addedNotice) this.refs.addedNotice.hidden = !(this.#addedFrom !== null && this.#cart.count > this.#addedFrom);
    if (this.refs.app && !this.refs.app.hidden) this.#render(false);
  }

  /** Keeps the customer's place before they leave for another builder. */
  #remember() {
    this.#store(STORAGE_KEY, JSON.stringify(this.#answers()));
    // Stored again here: a builder opened on its own in between removes it.
    this.#store(`${RETURN_KEY}-url`, window.location.pathname + this.#viewQuery());
  }

  /**
   * @param {string} item
   * @returns {Detail} The item's details, created empty the first time.
   */
  #detail(item) {
    this.#details[item] ??= { quantity: '', choices: {} };
    return this.#details[item];
  }

  /**
   * The follow-up questions of an item, read from the section's templates.
   * @param {string} item
   * @returns {{ key: string, label: string, emailLabel: string }[]}
   */
  #questions(item) {
    const template = this.querySelector(`template[data-follow-up-template="${this.#followUp(item)}"]`);
    if (!(template instanceof HTMLTemplateElement)) return [];
    return Array.from(template.content.querySelectorAll('[data-choice]')).map((group) => {
      const element = /** @type {HTMLElement} */ (group);
      return {
        key: element.dataset.choice ?? '',
        label: (element.querySelector('[data-choice-label]')?.textContent ?? '').trim(),
        emailLabel: element.dataset.emailLabel ?? '',
      };
    });
  }

  /** @returns {Answers} Everything answered so far. */
  #answers() {
    const refs = this.refs;
    const value = (/** @type {{ value: string } | undefined} */ field) => (field?.value ?? '').trim();
    return {
      agencyType: this.#agencyType,
      items: [...this.#items],
      later: this.#items.filter((item) => this.#later.has(item)),
      details: Object.fromEntries(
        this.#detailedItems().map((item) => {
          const detail = this.#detail(item);
          return [item, { quantity: detail.quantity.trim(), choices: { ...detail.choices } }];
        })
      ),
      needs: value(refs.needs),
      agency: value(refs.agency),
      name: value(refs.name),
      title: value(refs.title),
      email: value(refs.email),
      phone: value(refs.phone),
      location: value(refs.location),
      date: value(refs.date),
      source: this.#source,
      sourceOther: this.#sourceNeedsDetails() ? value(refs.sourceOther) : '',
    };
  }

  /**
   * Checks one step.
   * @param {string} key
   * @returns {Record<string, string>} Field name to error message; empty when the step is fine.
   */
  #problems(key) {
    const data = this.dataset;
    const answers = this.#answers();
    /** @type {Record<string, string>} */
    const problems = {};

    if (key === 'agency' && !answers.agencyType) problems.agencyType = data.errorAgencyType ?? '';
    if (key === 'items' && !answers.items.length) problems.items = data.errorItems ?? '';
    if (key === 'details') {
      for (const [item, detail] of Object.entries(answers.details)) {
        if (detail.quantity !== '' && !/^\d+$/.test(detail.quantity)) problems[`quantity:${item}`] = data.errorQuantity ?? '';
      }
      // An order-now item is built now (it is in the cart) or put off on purpose. Without
      // an answer from the cart there is nothing to check against.
      for (const item of this.#items) {
        if (this.#cart && this.#ordered(item) && !this.#inCart(item) && !this.#later.has(item)) problems[`order:${item}`] = data.errorOrder ?? '';
      }
    }
    if (key === 'contact') {
      if (!answers.agency) problems.agency = data.errorAgency ?? '';
      if (!answers.name) problems.name = data.errorName ?? '';
      if (!EMAIL_PATTERN.test(answers.email)) problems.email = data.errorEmail ?? '';
      // A date typed by hand can be in the past or incomplete; the picker alone does not stop that.
      const date = this.refs.date;
      if (date && (date.validity.badInput || (answers.date !== '' && answers.date < this.#today()))) problems.date = data.errorDate ?? '';
    }
    return problems;
  }

  /**
   * Shows or clears the messages of a step. A message that appears is announced.
   * @param {string} key
   * @param {Record<string, string>} problems
   * @returns {HTMLElement | null} The first field with a problem, to move focus to.
   */
  #showProblems(key, problems) {
    const panel = this.#panel(key);
    if (!panel) return null;
    /** @type {HTMLElement | null} */
    let first = null;

    const set = (/** @type {Element | null} */ message, /** @type {HTMLElement | null} */ field, /** @type {string} */ text) => {
      if (!(message instanceof HTMLElement)) return;
      message.textContent = text;
      message.hidden = !text;
      if (field instanceof HTMLInputElement) field.setAttribute('aria-invalid', String(Boolean(text)));
      if (text && !first) first = field ?? panel.querySelector('.hat-builder__tile');
    };

    for (const message of panel.querySelectorAll('[data-error-for]')) {
      const name = message instanceof HTMLElement ? message.dataset.errorFor ?? '' : '';
      const field = /** @type {Record<string, HTMLElement | undefined>} */ (this.refs)[name] ?? null;
      set(message, field, problems[name] ?? '');
    }
    for (const card of panel.querySelectorAll('[data-detail]')) {
      if (!(card instanceof HTMLElement)) continue;
      const ordered = card.hasAttribute('data-order');
      const field = card.querySelector(ordered ? '[data-order-link]' : '[data-detail-quantity]');
      set(card.querySelector('[data-detail-error]'), field instanceof HTMLElement ? field : null, problems[`${ordered ? 'order' : 'quantity'}:${card.dataset.detail}`] ?? '');
    }
    return first;
  }

  /**
   * @param {string} key
   * @returns {boolean} True when the step is fine. Otherwise its messages are shown and focus moves to the first one.
   */
  #check(key) {
    const problems = this.#problems(key);
    const first = this.#showProblems(key, problems);
    if (!Object.keys(problems).length) return true;
    first?.focus();
    return false;
  }

  /**
   * One card per chosen item. A quoted item asks for a quantity and its follow-up questions;
   * an item ordered in another builder has a button to go there. Earlier answers are kept.
   */
  #buildDetails() {
    const { details, detailTemplate, noDetails, needs } = this.refs;
    if (!details || !detailTemplate) return;
    const quoted = this.#detailedItems();
    const items = this.#items.filter((item) => this.#ordered(item) || quoted.includes(item));
    if (noDetails) noDetails.hidden = items.length > 0;
    const kind = (/** @type {boolean} */ ordered, /** @type {string | undefined} */ item) => `${ordered ? 'order' : 'quote'}:${item}`;
    const built = Array.from(details.children).map((card) => (card instanceof HTMLElement ? kind(card.hasAttribute('data-order'), card.dataset.detail) : ''));
    if (built.join('\n') !== items.map((item) => kind(this.#ordered(item), item)).join('\n')) {
      details.replaceChildren(
        ...items.map((item, index) => {
          const ordered = this.#ordered(item);
          const source = (ordered && this.refs.orderTemplate) || detailTemplate;
          const card = /** @type {HTMLElement} */ (source.content.firstElementChild?.cloneNode(true));
          const id = `${needs?.id ?? 'public-safety'}-detail-${index}`;
          const followUp = this.#followUp(item);
          card.dataset.detail = item;

          const title = card.querySelector('[data-detail-title]');
          if (title instanceof HTMLElement) {
            title.textContent = item;
            title.id = `${id}-title`;
            card.setAttribute('aria-labelledby', title.id);
          }

          // Ordered elsewhere: a link to that builder, marked so its bar can bring the customer back.
          const link = card.querySelector('[data-order-link]');
          if (link instanceof HTMLAnchorElement) {
            const url = new URL(this.#itemTile(item)?.dataset.link ?? '', window.location.origin);
            url.searchParams.set('from', 'public-safety');
            link.href = url.href;
          }

          const input = card.querySelector('[data-detail-quantity]');
          const label = card.querySelector('[data-detail-quantity-label]');
          const message = card.querySelector('[data-detail-error]');
          if (input instanceof HTMLInputElement && label instanceof HTMLLabelElement && message instanceof HTMLElement) {
            input.id = `${id}-quantity`;
            input.value = this.#detail(item).quantity;
            label.htmlFor = input.id;
            label.textContent = (followUp === 'badges' ? this.dataset.badgeQuantityLabel : this.dataset.quantityLabel) ?? '';
            message.id = `${id}-error`;
            input.setAttribute('aria-describedby', message.id);
          }

          const template = this.querySelector(`template[data-follow-up-template="${followUp}"]`);
          const box = card.querySelector('[data-detail-choices]');
          if (template instanceof HTMLTemplateElement && box) {
            box.append(template.content.cloneNode(true));
            box.querySelectorAll('[data-choice]').forEach((group, position) => {
              const name = group.querySelector('[data-choice-label]');
              if (!(name instanceof HTMLElement)) return;
              name.id = `${id}-choice-${position}`;
              group.setAttribute('aria-labelledby', name.id);
            });
          }
          return card;
        })
      );
    }

    // Mark what is already in the cart, and the chosen answers.
    for (const card of details.querySelectorAll('[data-detail]')) {
      if (!(card instanceof HTMLElement)) continue;
      if (card.hasAttribute('data-order')) {
        const inCart = this.#inCart(card.dataset.detail ?? '');
        const later = !inCart && this.#later.has(card.dataset.detail ?? '');
        const show = (/** @type {string} */ selector, /** @type {boolean} */ visible) => {
          const element = card.querySelector(selector);
          if (element instanceof HTMLElement) element.hidden = !visible;
          return element;
        };
        const tag = show('[data-order-tag]', inCart || later);
        if (tag) tag.textContent = (inCart ? this.dataset.inCartLabel : this.dataset.laterLabel) ?? '';
        show('[data-order-first]', !inCart);
        show('[data-order-more]', inCart);
        // Once it is in the cart there is nothing to put off.
        show('[data-order-later]', !inCart)?.setAttribute('aria-pressed', String(later));
        continue;
      }
      const choices = this.#detail(card.dataset.detail ?? '').choices;
      for (const button of card.querySelectorAll('[data-choice-value]')) {
        const group = button.closest('[data-choice]');
        if (!(button instanceof HTMLElement) || !(group instanceof HTMLElement)) continue;
        button.setAttribute('aria-pressed', String(choices[group.dataset.choice ?? ''] === button.dataset.choiceValue));
      }
    }
  }

  /**
   * The answers as short text per step, for the selections and the review.
   * @returns {Record<string, string[]>}
   */
  #lines() {
    const answers = this.#answers();
    const data = this.dataset;
    const notSure = data.notSure ?? '';
    const label = (/** @type {HTMLElement | undefined} */ field) => {
      const element = field ? this.querySelector(`label[for="${field.id}"]`) : null;
      // The label's own words, without the "(optional)" note inside it.
      return element ? (element.firstChild?.textContent ?? '').trim() : '';
    };
    const pair = (/** @type {string} */ name, /** @type {string} */ value) => (value ? [`${name}: ${value}`] : []);
    const refs = this.refs;

    return {
      agency: answers.agencyType ? [answers.agencyType] : [],
      items: answers.items.length ? [answers.items.join(', ')] : [],
      details: [
        ...this.#items
          .filter((item) => this.#ordered(item))
          .map((item) => `${item} — ${(this.#inCart(item) ? data.inCartLabel : this.#later.has(item) ? data.laterLabel : data.orderLabel) ?? ''}`),
        ...Object.entries(answers.details).map(([item, detail]) => {
        const quantityName = (this.#followUp(item) === 'badges' ? data.badgeQuantityLabel : data.quantityLabel) ?? '';
        const parts = [`${quantityName}: ${detail.quantity || notSure}`];
        for (const question of this.#questions(item)) {
          // "Do you have artwork?" reads as "Do you have artwork: Yes".
          if (detail.choices[question.key]) parts.push(`${question.label.replace(/\?$/, '')}: ${detail.choices[question.key]}`);
        }
          return `${item} — ${parts.join(', ')}`;
        }),
      ],
      needs: answers.needs ? [answers.needs] : [],
      contact: [
        ...pair(label(refs.agency), answers.agency),
        ...pair(label(refs.name), answers.name),
        ...pair(label(refs.title), answers.title),
        ...pair(label(refs.email), answers.email),
        ...pair(label(refs.phone), answers.phone),
        ...pair(label(refs.location), answers.location),
        ...pair(label(refs.date), this.#readableDate(answers.date)),
      ],
      source: answers.source ? [answers.sourceOther ? `${answers.source}: ${answers.sourceOther}` : answers.source] : [],
    };
  }

  /**
   * @param {string} date - YYYY-MM-DD.
   * @param {string} [locale]
   * @returns {string} For example "November 5, 2026".
   */
  #readableDate(date, locale) {
    const [year, month, day] = date.split('-').map(Number);
    if (!year || !month || !day) return '';
    return new Date(year, month - 1, day).toLocaleDateString(locale || document.documentElement.lang || undefined, { dateStyle: 'long' });
  }

  /** @param {boolean} moved - True when the step changed, to slide it in and move focus. */
  #render(moved) {
    const refs = this.refs;
    const steps = this.#steps();
    this.#current = Math.min(this.#current, steps.length - 1);
    const key = steps[this.#current] ?? 'agency';
    if (key === 'details') this.#buildDetails();

    for (const panel of this.querySelectorAll('[data-panel]')) {
      if (!(panel instanceof HTMLElement)) continue;
      const active = panel.dataset.panel === key;
      panel.hidden = !active;
      panel.removeAttribute('data-enter');
      if (active && moved) {
        // Restart the slide-in animation.
        void panel.offsetWidth;
        panel.setAttribute('data-enter', '');
        const heading = panel.querySelector('.public-safety-builder__panel-heading');
        if (heading instanceof HTMLElement) heading.focus();
      }
    }

    for (const tile of this.#tiles('agency')) tile.setAttribute('aria-pressed', String(tile.dataset.value === this.#agencyType));
    for (const tile of this.#tiles('items')) tile.setAttribute('aria-pressed', String(this.#items.includes(tile.dataset.value ?? '')));
    for (const tile of this.#tiles('source')) tile.setAttribute('aria-pressed', String(tile.dataset.value === this.#source));
    if (refs.sourceOtherField) refs.sourceOtherField.hidden = !this.#sourceNeedsDetails();

    const lines = this.#lines();
    this.#renderChips(lines);
    if (key === 'review') this.#renderSummary(lines);

    refs.progress?.replaceChildren(
      ...steps.map((step, index) => {
        const segment = document.createElement('span');
        if (index < this.#current) segment.setAttribute('data-done', '');
        return segment;
      })
    );

    if (refs.back) refs.back.disabled = this.#current === 0;
    // Starting over makes sense once there is something to clear.
    if (refs.restart) refs.restart.hidden = this.#current === 0;
    if (this.#current === 0) this.#askRestart(false);

    // The review step ends with the send button instead. With items in the cart it also leads there.
    if (refs.next) refs.next.hidden = key === 'review';
    if (refs.submit) refs.submit.textContent = (this.#cart?.lines.length ? this.dataset.sendCartLabel : this.dataset.sendLabel) ?? '';
  }

  /**
   * The answers so far. Tapping one goes back to that step.
   * @param {Record<string, string[]>} lines
   */
  #renderChips(lines) {
    const { chips, empty } = this.refs;
    if (!chips) return;
    const built = [];
    for (const key of this.#steps()) {
      const full = (lines[key] ?? []).join(' · ');
      if (!full) continue;
      // The selections stay short; the review shows everything.
      const text = full.length > 80 ? `${full.slice(0, 77).trimEnd()}…` : full;

      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'public-safety-builder__chip';
      chip.dataset.goto = key;

      const label = document.createElement('span');
      label.className = 'public-safety-builder__chip-label';
      label.textContent = this.#panel(key)?.dataset.label ?? '';
      const value = document.createElement('span');
      value.className = 'public-safety-builder__chip-value';
      value.textContent = text;
      chip.append(label, value);
      built.push(chip);
    }
    chips.replaceChildren(...built);
    if (empty) empty.hidden = built.length > 0;
  }

  /**
   * The review: what is in the cart, then every step's answers with a button to change them.
   * @param {Record<string, string[]>} lines
   */
  #renderSummary(lines) {
    const { summary } = this.refs;
    if (!summary) return;
    const data = this.dataset;
    const quoting = this.#quoteItems();
    const later = this.#laterItems();
    // The review says which items are sent for a quote and which will be built later. The cart has its own block.
    const sorted = [
      ...(quoting.length ? [`${data.quoteLabel ?? ''}: ${quoting.join(', ')}`] : []),
      ...(later.length ? [`${data.laterHeading ?? ''}: ${later.join(', ')}`] : []),
    ];
    const shown = { ...lines, items: sorted.length ? sorted : lines.items };

    const groups = [];
    const cartText = this.#cartText();
    if (cartText.length) {
      const group = document.createElement('div');
      group.className = 'public-safety-builder__summary-group';
      const title = document.createElement('h4');
      title.className = 'public-safety-builder__summary-title';
      title.textContent = data.cartLabel ?? '';
      const list = document.createElement('ul');
      list.className = 'public-safety-builder__summary-list';
      for (const entry of cartText) {
        const item = document.createElement('li');
        item.textContent = entry;
        list.append(item);
      }
      group.append(title, list);
      groups.push(group);
    }

    summary.replaceChildren(
      ...groups,
      ...this.#steps()
        .filter((key) => key !== 'review')
        .map((key) => this.#summaryGroup(key, shown[key] ?? []))
    );
  }

  /**
   * @param {string} key - The step.
   * @param {string[]} entries - Its answers.
   * @returns {HTMLElement} One block of the review, with its Edit button.
   */
  #summaryGroup(key, entries) {
    const name = this.#panel(key)?.dataset.label ?? '';
    const group = document.createElement('div');
    group.className = 'public-safety-builder__summary-group';

    const title = document.createElement('h4');
    title.className = 'public-safety-builder__summary-title';
    title.textContent = name;

    const list = document.createElement('ul');
    list.className = 'public-safety-builder__summary-list';
    for (const entry of entries.length ? entries : ['—']) {
      const item = document.createElement('li');
      item.textContent = entry;
      list.append(item);
    }

    const edit = document.createElement('button');
    edit.type = 'button';
    edit.className = 'button-secondary public-safety-builder__edit';
    edit.dataset.goto = key;
    edit.textContent = this.dataset.editLabel ?? '';
    edit.setAttribute('aria-label', `${this.dataset.editLabel ?? ''}: ${name}`);

    group.append(title, list, edit);
    return group;
  }

  /**
   * Opens or closes the "Start over" question. Its wording depends on whether the cart has items.
   * @param {boolean} open
   */
  #askRestart(open) {
    const { restart, restartBox, restartQuestion, restartError } = this.refs;
    if (!restart || !restartBox) return;
    const hasItems = Boolean(this.#cart?.lines.length);
    for (const part of restartBox.querySelectorAll('[data-restart-empty]')) if (part instanceof HTMLElement) part.hidden = hasItems;
    for (const part of restartBox.querySelectorAll('[data-restart-cart]')) if (part instanceof HTMLElement) part.hidden = !hasItems;
    if (restartError) restartError.hidden = true;
    restartBox.hidden = !open;
    restart.setAttribute('aria-expanded', String(open));
    if (open) restartQuestion?.focus();
  }

  /**
   * Clears every answer and the saved place, and goes back to the first step.
   * @param {boolean} emptyCart - True when the customer also chose to empty the cart.
   */
  async #startOver(emptyCart) {
    const refs = this.refs;
    if (emptyCart) {
      // Only after the customer picked this answer. If it fails, nothing is cleared.
      try {
        const response = await fetch(`${this.dataset.cartClearUrl || '/cart/clear'}.js`, { method: 'POST', headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('cart');
        this.#cart = { lines: [], count: 0, cents: 0, currency: this.#cart?.currency ?? '' };
      } catch {
        if (refs.restartError) {
          refs.restartError.textContent = this.dataset.restartError ?? '';
          refs.restartError.hidden = false;
        }
        return;
      }
    }

    this.#agencyType = '';
    this.#items = [];
    this.#details = {};
    this.#later = new Set();
    this.#source = '';
    this.#addedFrom = null;
    for (const field of [refs.needs, refs.agency, refs.name, refs.title, refs.email, refs.phone, refs.location, refs.date, refs.sourceOther]) {
      if (field) field.value = '';
    }
    refs.details?.replaceChildren();
    if (refs.addedNotice) refs.addedNotice.hidden = true;
    for (const key of this.#steps()) this.#showProblems(key, {});

    // The saved place, and what a visit to another builder may have left behind.
    this.#forget();
    this.#store(`${RETURN_KEY}-added`, null);
    this.#store(`${RETURN_KEY}-cart`, null);

    this.#askRestart(false);
    this.#current = 0;
    // Moves focus to the first step's heading.
    this.#render(true);
    if (refs.announce) refs.announce.textContent = this.dataset.restartDone ?? '';
  }

  /** @param {number} index */
  #go(index) {
    this.#askRestart(false);
    if (this.refs.announce) this.refs.announce.textContent = '';
    this.#current = Math.min(this.#steps().length - 1, Math.max(0, index));
    this.#render(true);
  }

  /** @param {Event} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const { back, next } = this.refs;

    const tile = target.closest('.hat-builder__tile');
    if (tile instanceof HTMLElement && this.contains(tile)) {
      const step = tile.dataset.step ?? '';
      const value = tile.dataset.value ?? '';
      if (step === 'agency') {
        // One answer: choosing it moves on.
        this.#agencyType = value;
        this.#showProblems('agency', {});
        this.#go(this.#steps().indexOf('agency') + 1);
      } else if (step === 'items') {
        // Several answers: a tap adds or removes one.
        this.#items = this.#items.includes(value) ? this.#items.filter((item) => item !== value) : [...this.#items, value];
        if (this.#items.length) this.#showProblems('items', {});
        this.#render(false);
      } else if (step === 'source') {
        // A second tap takes the answer back; the question is optional.
        this.#source = this.#source === value ? '' : value;
        this.#render(false);
        if (this.#sourceNeedsDetails()) this.refs.sourceOther?.focus();
      }
      return;
    }

    // Leaving for another builder: it is being built now after all. Keep the customer's place
    // first; the link itself does the rest.
    const leaving = target.closest('[data-order-link]');
    if (leaving) {
      const card = leaving.closest('[data-detail]');
      if (card instanceof HTMLElement) this.#later.delete(card.dataset.detail ?? '');
      this.#remember();
      return;
    }

    // "Build later" on an order-now item. A second tap takes it back.
    const later = target.closest('[data-order-later]');
    const laterCard = later?.closest('[data-detail]');
    if (later && laterCard instanceof HTMLElement) {
      const item = laterCard.dataset.detail ?? '';
      if (this.#later.has(item)) this.#later.delete(item);
      else this.#later.add(item);
      this.#buildDetails();
      // A message that is showing follows the new answer.
      if (laterCard.querySelector('[data-detail-error]:not([hidden])')) this.#showProblems('details', this.#problems('details'));
      this.#renderChips(this.#lines());
      return;
    }

    // A follow-up answer inside an item's card. A second tap takes it back.
    const choice = target.closest('[data-choice-value]');
    const group = choice?.closest('[data-choice]');
    const card = choice?.closest('[data-detail]');
    if (choice instanceof HTMLElement && group instanceof HTMLElement && card instanceof HTMLElement) {
      const choices = this.#detail(card.dataset.detail ?? '').choices;
      const key = group.dataset.choice ?? '';
      const value = choice.dataset.choiceValue ?? '';
      if (choices[key] === value) delete choices[key];
      else choices[key] = value;
      this.#buildDetails();
      this.#renderChips(this.#lines());
      return;
    }

    // "Start over": the button asks, and one of the answers acts.
    if (this.refs.restart?.contains(target)) {
      this.#askRestart(Boolean(this.refs.restartBox?.hidden));
      return;
    }
    const answer = target.closest('[data-restart]');
    if (answer instanceof HTMLElement) {
      if (answer.dataset.restart === 'cancel') {
        this.#askRestart(false);
        this.refs.restart?.focus();
      } else {
        this.#startOver(answer.dataset.restart === 'empty');
      }
      return;
    }

    const jump = target.closest('[data-goto]');
    if (jump instanceof HTMLElement) {
      this.#go(this.#steps().indexOf(jump.dataset.goto ?? ''));
      return;
    }

    if (back?.contains(target)) {
      this.#go(this.#current - 1);
      return;
    }
    if (next?.contains(target) && this.#check(this.#steps()[this.#current] ?? '')) this.#go(this.#current + 1);
  };

  /**
   * Typing clears that field's message once it is fixed, and updates the selections.
   * @param {Event} event
   */
  #onInput = (event) => {
    const input = event.target;
    if (input instanceof HTMLInputElement && input.matches('[data-detail-quantity]')) {
      const card = input.closest('[data-detail]');
      if (card instanceof HTMLElement) this.#detail(card.dataset.detail ?? '').quantity = input.value;
    }
    const key = this.#steps()[this.#current] ?? '';
    const panel = this.#panel(key);
    // Only refresh messages that are already showing; nothing new appears while typing.
    if (panel?.querySelector('[role="alert"]:not([hidden])')) {
      const problems = this.#problems(key);
      const shown = Object.fromEntries(
        Object.entries(problems).filter(([name]) => {
          const card = Array.from(panel.querySelectorAll('[data-detail]')).find(
            (candidate) => candidate instanceof HTMLElement && `quantity:${candidate.dataset.detail}` === name
          );
          const message = card ? card.querySelector('[data-detail-error]') : panel.querySelector(`[data-error-for="${name}"]`);
          return message instanceof HTMLElement && !message.hidden;
        })
      );
      this.#showProblems(key, shown);
    }
    this.#renderChips(this.#lines());
  };

  /**
   * Enter in a field goes to the next step instead of sending a half-finished form.
   * @param {KeyboardEvent} event
   */
  #onKeydown = (event) => {
    if (event.key !== 'Enter' || !(event.target instanceof HTMLInputElement)) return;
    event.preventDefault();
    this.refs.next?.click();
  };

  /** @param {boolean} sending */
  #setSending(sending) {
    this.#sending = sending;
    const { submit, sending: status } = this.refs;
    if (submit) submit.setAttribute('aria-disabled', String(sending));
    if (status) status.textContent = sending ? status.dataset.sending ?? '' : '';
  }

  /** @returns {boolean} True when saved answers were put back. */
  #restore() {
    try {
      const saved = /** @type {Answers | null} */ (JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) ?? 'null'));
      if (!saved || typeof saved !== 'object') return false;
      const refs = this.refs;
      const known = (/** @type {string} */ step, /** @type {unknown} */ value) =>
        typeof value === 'string' && this.#tiles(step).some((tile) => tile.dataset.value === value);

      this.#agencyType = known('agency', saved.agencyType) ? saved.agencyType : '';
      this.#items = Array.isArray(saved.items) ? saved.items.filter((item) => known('items', item)) : [];
      this.#source = known('source', saved.source) ? saved.source : '';
      this.#later = new Set(Array.isArray(saved.later) ? saved.later.filter((item) => this.#items.includes(item)) : []);
      this.#details = {};
      for (const [item, detail] of Object.entries(saved.details && typeof saved.details === 'object' ? saved.details : {})) {
        if (!detail || typeof detail !== 'object') continue;
        this.#details[item] = {
          quantity: typeof detail.quantity === 'string' ? detail.quantity : '',
          choices: detail.choices && typeof detail.choices === 'object' ? { ...detail.choices } : {},
        };
      }
      const fill = (/** @type {{ value: string } | undefined} */ field, /** @type {unknown} */ value) => {
        if (field && typeof value === 'string') field.value = value;
      };
      fill(refs.needs, saved.needs);
      fill(refs.agency, saved.agency);
      fill(refs.name, saved.name);
      fill(refs.title, saved.title);
      fill(refs.email, saved.email);
      fill(refs.phone, saved.phone);
      fill(refs.location, saved.location);
      fill(refs.date, saved.date);
      fill(refs.sourceOther, saved.sourceOther);
      return true;
    } catch {
      return false;
    }
  }

  #forget() {
    this.#store(STORAGE_KEY, null);
  }

  /**
   * The message the store receives: a subject line, then one labeled line per answer.
   * @param {Answers} answers
   * @returns {string}
   */
  #message(answers) {
    const quoting = this.#quoteItems();
    const later = this.#laterItems();
    const cartText = this.#cartText();
    const lines = [`Public Safety quote: ${answers.agency}`];
    // Something still needs an answer from us, beyond what is already in the cart.
    if (quoting.length || later.length) lines.push('Follow up by email requested.');
    const add = (/** @type {string} */ label, /** @type {string} */ value) => {
      if (value) lines.push(`${label}: ${value}`);
    };

    if (quoting.length) {
      lines.push('', `Quote request: ${quoting.join(', ')}`);
      for (const [item, detail] of Object.entries(answers.details)) {
        add(`${item} - ${this.#followUp(item) === 'badges' ? 'Number of badges' : 'Quantity'}`, detail.quantity || 'Not sure');
        for (const question of this.#questions(item)) add(`${item} - ${question.emailLabel}`, detail.choices[question.key] ?? '');
      }
    }
    if (later.length) lines.push('', `Interested in (will build later): ${later.join(', ')}`);
    if (cartText.length) lines.push('', 'In cart:', ...cartText.map((line) => `- ${line}`));

    lines.push('');
    add('Agency type', answers.agencyType);
    add('Agency or department', answers.agency);
    add('Name', answers.name);
    add('Title or role', answers.title);
    add('Email', answers.email);
    add('Phone', answers.phone);
    add('City and state', answers.location);
    add('Date needed', answers.date ? `${this.#readableDate(answers.date, 'en-US')} (${answers.date})` : '');
    add('Heard about us', answers.sourceOther ? `${answers.source} - ${answers.sourceOther}` : answers.source);
    add('Notes', answers.needs.replace(/\s*\n\s*/g, ' / '));
    return lines.join('\n');
  }

  /**
   * Replaces the form with a message, without sending anything.
   * @param {HTMLElement | undefined} message
   */
  #replaceForm(message) {
    const { app, intro, serverError, addedNotice } = this.refs;
    if (app) app.hidden = true;
    if (intro) intro.hidden = true;
    if (addedNotice) addedNotice.hidden = true;
    if (serverError) serverError.hidden = true;
    if (message) {
      message.hidden = false;
      message.focus();
    }
  }

  /** @param {SubmitEvent} event */
  #onSubmit = (event) => {
    if (this.#sending) {
      event.preventDefault();
      return;
    }

    // Every step is checked again; the first one with a problem is shown.
    const steps = this.#steps();
    for (const key of steps) {
      if (Object.keys(this.#problems(key)).length === 0) continue;
      event.preventDefault();
      this.#current = steps.indexOf(key);
      this.#render(true);
      this.#check(key);
      return;
    }

    // Spam checks. A person never fills the hidden field and cannot finish this quickly.
    const tooFast = Date.now() - this.#started < MINIMUM_SECONDS * 1000;
    if ((this.refs.trap?.value ?? '') !== '' || tooFast) {
      event.preventDefault();
      this.#replaceForm(this.refs.thanks);
      return;
    }

    const answers = this.#answers();
    if (this.refs.body) this.refs.body.value = this.#message(answers);
    this.#store(STORAGE_KEY, JSON.stringify(answers));
    // With something in the cart, the page that follows a successful send goes on to the cart.
    this.#store(`${RETURN_KEY}-cart`, this.#cart?.lines.length ? '1' : null);
    this.#setSending(true);
  };
}

if (!customElements.get('public-safety-builder')) {
  customElements.define('public-safety-builder', PublicSafetyBuilder);
}
